import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Container, Typography, Tabs, Tab } from '@mui/material';
import { GraphViewer } from '../components/graph/GraphViewer';
import { UserProfile, PasswordChange, PreferencesPanel, UserList, UserForm } from '@diggens/user-management';
import { useCurrentUser } from '@diggens/user-management';
import type { User } from '@diggens/user-management';
import { InstalledApps } from './InstalledApps';

/** Each tab is a route segment under /settings so it can be linked to; the first is the bare /settings. */
export type SettingsSection = 'general' | 'profile' | 'users' | 'apps' | 'graph';

interface SettingsTab {
  section: SettingsSection;
  label: string;
}

export function settingsPath(section: SettingsSection): string {
  return section === 'general' ? '/settings' : `/settings/${section}`;
}

export function Settings() {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const isAdmin = user?.role && ['super_admin', 'admin', 'billing_admin'].includes(user.role);
  const isSuperAdmin = user?.role && ['super_admin'].includes(user.role);

  const tabs: SettingsTab[] = [
    { section: 'general', label: 'General' },
    { section: 'profile', label: 'User Profile' },
    ...(isAdmin ? [{ section: 'users', label: 'User Management' } as const] : []),
    { section: 'apps', label: 'Installed Apps' },
    ...(isSuperAdmin ? [{ section: 'graph', label: 'Graph Viewer' } as const] : []),
  ];
  const active: SettingsSection = tabs.some((tab) => tab.section === section) ? (section as SettingsSection) : 'general';

  const [userFormOpen, setUserFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>();

  const handleAddUser = () => {
    setSelectedUser(undefined);
    setUserFormOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setUserFormOpen(true);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={active} onChange={(_, value: SettingsSection) => navigate(settingsPath(value))}>
          {tabs.map((tab) => (
            <Tab key={tab.section} value={tab.section} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {active === 'general' && <Typography color="text.secondary">General settings coming soon.</Typography>}

      {active === 'profile' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <UserProfile />
          <PasswordChange />
          <PreferencesPanel />
        </Box>
      )}

      {isAdmin && active === 'users' && (
        <Box>
          <UserList onAddUser={handleAddUser} onEditUser={handleEditUser} />
          <UserForm
            open={userFormOpen}
            onClose={() => {
              setUserFormOpen(false);
              setSelectedUser(undefined);
            }}
            user={selectedUser}
          />
        </Box>
      )}

      {active === 'apps' && <InstalledApps />}

      {isSuperAdmin && active === 'graph' && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Visual representation of your tenant's node and edge relationships.
          </Typography>
          <GraphViewer />
        </Box>
      )}
    </Container>
  );
}
